import {NullableString} from "../../../../types/nullable-types";
import {StandardBookingSearchStrategy} from "../common/standard-booking-search.strategy";
import {BookingSearchStrategyType} from "../booking-search.strategy.interface";
import {FormFields} from "../../../../models/forms/form-field.interface";
import {
    IBookingSearchByLastNameRequest,
    IBookingSearchPagination
} from "../../../airline-webapi/requests/booking-search.requests";
import {IBookingSearchResultRecord} from "../../../airline-webapi/responses/booking-search.response";
import React from "react";
import {SearchByLastNameComponent} from "./search-by-last-name.component";
import {
    IBookingSearchCommonFields,
    IBookingSearchCommonPersistedFields
} from "../common/booking-search-common-fields.interface";
import {personOutline} from "ionicons/icons";

interface ISearchByLastNameFields extends IBookingSearchCommonFields {
    lastName: string;
    firstName: string;
}

interface ISearchByLastNamePersistedFields extends IBookingSearchCommonPersistedFields {
    lastName: string;
    firstName: NullableString;
}

export class SearchByLastNameStrategy extends StandardBookingSearchStrategy<ISearchByLastNameFields, ISearchByLastNamePersistedFields> {

    get type(): BookingSearchStrategyType {
        return BookingSearchStrategyType.ByLastName;
    }

    get name(): string {
        return this.services.language.translate("By last name");
    }

    get resultsDescription(): string {
        return this.services.language.translate("Results by last name");
    }

    protected _getIonIconName(): NullableString {
        return personOutline;
    }

    protected _createFilterFields(persistedFields: ISearchByLastNamePersistedFields): FormFields<ISearchByLastNameFields> {
        return {
            lastName: this._createField<string>({
                fieldName: () => this.services.language.translate("Last name"),
                isRequired: true,
                defaultValue: persistedFields?.lastName ?? null
            }),
            firstName: this._createField<string>({
                fieldName: () => this.services.language.translate("First name"),
                isRequired: false,
                defaultValue: persistedFields?.firstName ?? null
            }),
            ...this._createCommonFields(persistedFields)
        };
    }

    renderSearchFields(): React.ReactElement {
        return <SearchByLastNameComponent strategy={this} />;
    }

    async _searchBookings(sessionToken: string, pagination: IBookingSearchPagination): Promise<IBookingSearchResultRecord[]> {
        const request: IBookingSearchByLastNameRequest = {
            dotRezToken: sessionToken,
            lastName: this.fields.lastName.value!,
            ...(this.fields.firstName.value && { firstName: this.fields.firstName.value }),
            filters: this._createCommonFilters(pagination)
        };

        const response = await this.services.airlineWebapi.agencyBookingSearchByLastName(request);
        return response.results;
    }

    protected _collectFieldsValuesForStorage(): ISearchByLastNamePersistedFields {
        return {
            lastName: this.fields.lastName.value ?? "",
            firstName: this.fields.firstName.value,
            ...this._collectCommonFieldsValuesForStorage()
        };
    }
}
